"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Menu, X, ArrowRight, ArrowUpRight, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { trackEvent, trackMetaEvent } from "@/lib/analytics";

const buildAppUrl = (base: string): string => {
  if (typeof window === "undefined") return base;
  try {
    const stored = window.localStorage.getItem("mindtale_attribution_last_v1");
    if (!stored) return base;
    const attr = JSON.parse(stored) as Record<string, string | null>;
    const url = new URL(base);
    ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
     "fbclid", "gclid", "gbraid", "wbraid"].forEach((key) => {
      if (attr[key]) url.searchParams.set(key, attr[key]!);
    });
    return url.toString();
  } catch {
    return base;
  }
};

// ─── Soluții dropdown ────────────────────────────────────────────────────────

const solutiiItems = [
  { label: "Sesiune de cunoaștere", href: "/sesiune-cunoastere", desc: "20 min gratis cu un terapeut real", badge: "Nou" },
  { label: "Găsește terapeut", href: "/gaseste-terapeut", desc: "Chestionar de 4 pași" },
  { label: "Sesiune cu Zenyra", href: "/zenyra", desc: "20 min cu asistenta AI" },
  { label: "Toți terapeuții", href: "/terapeuti", desc: "Echipa noastră acreditată COPSI" },
  { label: "Teste psihologice", href: "/teste", desc: "Anxietate, depresie, burnout" },
];

// ─── Despre dropdown ─────────────────────────────────────────────────────────

const despreItems = [
  { label: "Despre noi", href: "/despre-noi", desc: "Echipa și povestea Mindtale" },
  { label: "Fondul de Terapie", href: "/fondul-social", desc: "Cum dă mai departe fiecare ședință" },
  { label: "Cariere", href: "/cariere", desc: "Lucrează cu noi" },
  { label: "Contact", href: "/contact", desc: "Scrie-ne — răspundem repede" },
];

// ─── Pentru companii dropdown ────────────────────────────────────────────────

const companiiItems = [
  {
    label: "Program ReMind",
    href: "/remind",
    desc: "Retreat corporate pentru manageri",
    accent: true,
  },
  {
    label: "Programe pentru organizații",
    href: "/colaborare/organizatii",
    desc: "Parteneriate corporate, EAP și CSR",
  },
  {
    label: "Parteneriat pentru terapeuți",
    href: "/colaborare/terapeuti",
    desc: "Intră în rețeaua Mindtale",
  },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname() ?? "";
  /* The chat owns the bottom of the screen with its composer, so the dock
     would sit on top of it. A therapist profile has its own bottom bar
     (book this therapist), which replaces the generic one. Everywhere else it
     is always available. */
  const dockVisible =
    !pathname.startsWith("/zenyra") && !/^\/terapeuti\/[^/]+\/?$/.test(pathname);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleAppOpen = (location: string) => {
    trackEvent("cta_click", { cta_type: "enter_app", location, label: "Intră în cont" });
    trackMetaEvent("InitiateSignup", { signup_type: "client", location });
    window.open(buildAppUrl("https://app.mindtale.ro"), "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <header className={cn("sticky top-0 z-[60] transition-[padding] duration-500", scrolled && "px-3 pt-2.5 sm:pt-3")}>
        {/* Morphing island: full-width & transparent at top, floating glass
            pill once you scroll. */}
        <div
          className={cn(
            "mx-auto flex items-center justify-between transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
            scrolled
              ? "h-14 max-w-5xl rounded-full border border-[#264653]/8 bg-[#FDF9F5]/85 px-4 shadow-[0_10px_36px_rgba(38,70,83,0.14)] backdrop-blur-xl sm:px-5"
              : "h-16 max-w-[1320px] border border-transparent bg-transparent px-5 sm:h-[4.5rem] sm:px-8 lg:px-12 xl:px-16"
          )}
        >
          {/* Logo */}
          <Link
            href="/"
            className="flex flex-shrink-0 items-center lg:flex-1"
            aria-label="Mindtale — acasă"
          >
            <Image
              src="/logo.svg"
              alt="Mindtale"
              width={108}
              height={32}
              className="h-7 w-auto sm:h-8"
              priority
            />
          </Link>

          {/* Desktop nav — center */}
          <div className="hidden shrink-0 lg:flex lg:justify-center">
            <NavigationMenu viewport={false}>
              <NavigationMenuList className="gap-1">

                {/* Acasă */}
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link href="/" className="group relative inline-flex h-9 items-center px-3 text-[15px] font-medium text-[#264653]/80 transition hover:text-[#264653] after:absolute after:inset-x-3 after:bottom-1 after:h-[2px] after:origin-left after:scale-x-0 after:rounded-full after:bg-[#DC7050] after:transition-transform after:duration-300 hover:after:scale-x-100">
                      Acasă
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                {/* Soluții */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent px-3 text-[15px] font-medium text-[#264653]/80 hover:bg-[#264653]/6 hover:text-[#264653] data-[state=open]:bg-[#264653]/6 data-[state=open]:text-[#264653]">
                    Începe
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[340px] rounded-2xl border border-[#264653]/8 bg-white p-2 shadow-[0_12px_40px_rgba(38,70,83,0.12)]">
                      {solutiiItems.map((item) => (
                        <NavigationMenuLink asChild key={item.href}>
                          <Link
                            href={item.href}
                            className="group flex items-start gap-3 rounded-xl px-3 py-2.5 transition hover:bg-[#FDF9F5]"
                            onClick={() => {
                              if (item.href === "/zenyra") {
                                trackEvent("zenyra_cta_click", { location: "header_solutii_desktop" });
                              }
                            }}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-semibold text-[#264653]">
                                  {item.label}
                                </span>
                                {item.badge && (
                                  <span className="rounded-full bg-[#DC7050]/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[#DC7050]">
                                    {item.badge}
                                  </span>
                                )}
                              </div>
                              <span className="mt-0.5 block text-[12px] text-[#264653]/60">
                                {item.desc}
                              </span>
                            </div>
                            <ArrowRight className="mt-1 size-3.5 shrink-0 text-[#264653]/30 transition group-hover:translate-x-0.5 group-hover:text-[#2C969B]" />
                          </Link>
                        </NavigationMenuLink>
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Terapeuți */}
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link href="/terapeuti" className="group relative inline-flex h-9 items-center px-3 text-[15px] font-medium text-[#264653]/80 transition hover:text-[#264653] after:absolute after:inset-x-3 after:bottom-1 after:h-[2px] after:origin-left after:scale-x-0 after:rounded-full after:bg-[#DC7050] after:transition-transform after:duration-300 hover:after:scale-x-100">
                      Terapeuți
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                {/* Pentru companii */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent px-3 text-[15px] font-medium text-[#264653]/80 hover:bg-[#264653]/6 hover:text-[#264653] data-[state=open]:bg-[#264653]/6 data-[state=open]:text-[#264653]">
                    Companii
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[320px] rounded-2xl border border-[#264653]/8 bg-white p-2 shadow-[0_12px_40px_rgba(38,70,83,0.12)]">
                      {companiiItems.map((item) => (
                        <NavigationMenuLink asChild key={item.href}>
                          <Link
                            href={item.href}
                            className={cn(
                              "group flex flex-col rounded-xl px-3 py-2.5 transition",
                              item.accent
                                ? "bg-[#2C969B]/8 hover:bg-[#2C969B]/14"
                                : "hover:bg-[#FDF9F5]"
                            )}
                          >
                            <span
                              className={cn(
                                "text-sm font-semibold",
                                item.accent ? "text-[#2C969B]" : "text-[#264653]"
                              )}
                            >
                              {item.label}
                            </span>
                            <span className="mt-0.5 text-[12px] text-[#264653]/60">
                              {item.desc}
                            </span>
                          </Link>
                        </NavigationMenuLink>
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Resurse — a plain link, not a panel. The mega-menu listed a
                    subset of what /resurse now holds, and on the surface that
                    matters most it was unreachable: the mobile menu rendered
                    the same list as an accordion whose header was a <button>,
                    so there was no path to the page at all from a phone. */}
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link href="/resurse" className="group relative inline-flex h-9 items-center px-3 text-[15px] font-medium text-[#264653]/80 transition hover:text-[#264653] after:absolute after:inset-x-3 after:bottom-1 after:h-[2px] after:origin-left after:scale-x-0 after:rounded-full after:bg-[#DC7050] after:transition-transform after:duration-300 hover:after:scale-x-100">
                      Resurse
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>


                {/* Blog — was reachable only from the footer of the Resurse
                    mega-menu, which is two hovers deep for the one surface
                    that gets new content every week. */}
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link href="/blog" className="group relative inline-flex h-9 items-center px-3 text-[15px] font-medium text-[#264653]/80 transition hover:text-[#264653] after:absolute after:inset-x-3 after:bottom-1 after:h-[2px] after:origin-left after:scale-x-0 after:rounded-full after:bg-[#DC7050] after:transition-transform after:duration-300 hover:after:scale-x-100">
                      Blog
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                {/* Despre — cine suntem, fondul, contact */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent px-3 text-[15px] font-medium text-[#264653]/80 hover:bg-[#264653]/6 hover:text-[#264653] data-[state=open]:bg-[#264653]/6 data-[state=open]:text-[#264653]">
                    Despre
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[300px] rounded-2xl border border-[#264653]/8 bg-white p-2 shadow-[0_12px_40px_rgba(38,70,83,0.12)]">
                      {despreItems.map((item) => (
                        <NavigationMenuLink asChild key={item.href}>
                          <Link
                            href={item.href}
                            className="group flex items-start gap-3 rounded-xl px-3 py-2.5 transition hover:bg-[#FDF9F5]"
                          >
                            <div className="min-w-0 flex-1">
                              <span className="text-sm font-semibold text-[#264653]">
                                {item.label}
                              </span>
                              <span className="mt-0.5 block text-[12px] text-[#264653]/60">
                                {item.desc}
                              </span>
                            </div>
                            <ArrowRight className="mt-1 size-3.5 shrink-0 text-[#264653]/30 transition group-hover:translate-x-0.5 group-hover:text-[#2C969B]" />
                          </Link>
                        </NavigationMenuLink>
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Desktop right — Zenyra primary CTA + account text link */}
          <div className="hidden flex-shrink-0 items-center gap-3 lg:flex lg:flex-1 lg:justify-end">
            <a
              href="https://app.mindtale.ro"
              onClick={(e) => {
                e.preventDefault();
                handleAppOpen("header_desktop");
              }}
              className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-[13px] font-medium text-[#264653]/65 transition hover:text-[#264653]"
            >
              Ai cont? Intră aici
              <ArrowUpRight className="size-3.5" />
            </a>
            <Link
              href="/sesiune-cunoastere"
              onClick={() =>
                trackEvent("demo_promo_click", { location: "header_desktop" })
              }
              className="inline-flex h-10 shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-[#DC7050] px-4 text-[14px] font-semibold text-white shadow-[0_4px_14px_rgba(220,112,80,0.28)] transition hover:bg-[#c95e3f] hover:shadow-[0_6px_18px_rgba(220,112,80,0.38)]"
            >
              Sesiune de cunoaștere<span className="hidden xl:inline">&nbsp;gratuită</span>
              <ArrowRight className="size-4" />
            </Link>
          </div>

          {/* Mobile right — burger only */}
          <button
            type="button"
            onClick={() => setMenuOpen((p) => !p)}
            aria-label={menuOpen ? "Închide meniul" : "Deschide meniul"}
            aria-expanded={menuOpen}
            className="inline-flex size-11 items-center justify-center rounded-full text-[#264653] transition hover:bg-[#264653]/6 lg:hidden"
          >
            {menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>

        {/* Mobile menu panel — positioned absolute relative to the sticky
            header so it always sits right below the pill regardless of any
            promo bar pushing the header down. */}
        {menuOpen && (
          <div className="absolute inset-x-3 top-full z-[55] mx-auto mt-2 max-w-md rounded-2xl border border-[#264653]/8 bg-white shadow-[0_24px_60px_rgba(38,70,83,0.25)] lg:hidden">
            <div className="flex max-h-[calc(100svh-180px)] flex-col">
              <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-3">

                {/* Featured: free 20-min discovery session with a real
                    therapist. Highest-priority CTA on mobile. */}
                <Link
                  href="/sesiune-cunoastere"
                  onClick={() => {
                    trackEvent("demo_promo_click", {
                      location: "header_mobile_featured",
                    });
                    setMenuOpen(false);
                  }}
                  className="mb-2 flex items-center gap-3 rounded-xl bg-gradient-to-br from-[#2C969B]/10 to-[#FDF9F5] px-3 py-2.5 ring-1 ring-[#2C969B]/25"
                >
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#2C969B] text-white">
                    <Sparkles className="size-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[14px] font-semibold text-[#264653]">
                        Sesiune de cunoaștere
                      </span>
                      <span className="rounded-full bg-[#DC7050]/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-[#DC7050]">
                        Gratis
                      </span>
                    </div>
                    <p className="text-[11px] text-[#264653]/65">
                      20 min cu un terapeut real
                    </p>
                  </div>
                  <ArrowRight className="size-4 shrink-0 text-[#2C969B]" />
                </Link>

                <Link
                  href="/zenyra"
                  onClick={() => {
                    trackEvent("zenyra_cta_click", { location: "header_mobile_overlay" });
                    setMenuOpen(false);
                  }}
                  className="mb-3 flex items-center gap-3 rounded-xl bg-[#FDF9F5] px-3 py-2.5 ring-1 ring-[#264653]/8"
                >
                  <Image src="/Zenyra.svg" alt="" width={36} height={36} className="size-9 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[14px] font-semibold text-[#264653]">
                        Sesiune cu Zenyra
                      </span>
                    </div>
                    <p className="text-[11px] text-[#264653]/65">20 min cu asistenta AI</p>
                  </div>
                  <ArrowRight className="size-4 shrink-0 text-[#264653]/40" />
                </Link>

                <MobileLink href="/" onClose={() => setMenuOpen(false)}>
                  Acasă
                </MobileLink>

                {/* Soluții section */}
                <MobileSection title="Începe" />
                <MobileLink href="/gaseste-terapeut" onClose={() => setMenuOpen(false)}>
                  Găsește terapeut
                </MobileLink>
                <MobileLink href="/terapeuti" onClose={() => setMenuOpen(false)}>
                  Toți terapeuții
                </MobileLink>
                <MobileLink href="/teste" onClose={() => setMenuOpen(false)}>
                  Teste psihologice
                </MobileLink>

                {/* Pentru companii */}
                <MobileSection title="Companii" />
                {companiiItems.map((item) => (
                  <MobileLink
                    key={item.href}
                    href={item.href}
                    onClose={() => setMenuOpen(false)}
                    accent={item.accent}
                  >
                    {item.label}
                  </MobileLink>
                ))}

                <MobileSection title="Resurse" />
                <MobileLink href="/resurse" onClose={() => setMenuOpen(false)}>
                  Toate resursele
                </MobileLink>

                {/* Direct links */}
                <div className="mt-1.5 border-t border-[#264653]/8 pt-1.5">
                  <MobileLink href="/blog" onClose={() => setMenuOpen(false)}>Blog</MobileLink>
                  <MobileLink href="/despre-noi" onClose={() => setMenuOpen(false)}>Despre noi</MobileLink>
                  <MobileLink href="/contact" onClose={() => setMenuOpen(false)}>Contact</MobileLink>
                </div>
              </div>

              {/* Sticky bottom CTAs — always visible */}
              <div className="shrink-0 space-y-2 rounded-b-2xl border-t border-[#264653]/8 bg-white px-3 py-3">
                <Link
                  href="/sesiune-cunoastere"
                  onClick={() => {
                    trackEvent("demo_promo_click", {
                      location: "header_mobile_sticky",
                    });
                    setMenuOpen(false);
                  }}
                  className="flex h-11 w-full items-center justify-center rounded-full bg-[#DC7050] text-[14px] font-semibold text-white shadow-[0_6px_18px_rgba(220,112,80,0.30)] active:scale-[0.98]"
                >
                  Sesiune gratuită cu un terapeut
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    handleAppOpen("header_mobile_overlay");
                    setMenuOpen(false);
                  }}
                  className="flex h-11 w-full items-center justify-center gap-1.5 rounded-full border border-[#264653]/15 bg-transparent text-[13px] font-medium text-[#264653]/85 active:bg-[#264653]/5"
                >
                  Ai cont? Intră aici <ArrowUpRight className="size-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Backdrop for mobile menu — page visible behind, tap to close */}
      {menuOpen && (
        <button
          type="button"
          aria-label="Închide meniul"
          onClick={() => setMenuOpen(false)}
          className="fixed inset-0 z-40 bg-[#264653]/30 backdrop-blur-[2px] lg:hidden"
        />
      )}

      {/* Mobile bottom dock — the primary action lives where thumbs live.
          Appears once the hero CTA scrolls out of view; hidden while the
          menu overlay is open. */}
      <div
        className={cn(
          "fixed inset-x-0 bottom-0 z-[58] px-4 pb-[calc(env(safe-area-inset-bottom)+12px)] transition-all duration-300 lg:hidden",
          dockVisible && !menuOpen
            ? "translate-y-0 opacity-100"
            : "pointer-events-none translate-y-6 opacity-0"
        )}
      >
        <div className="mx-auto flex max-w-md items-center gap-2 rounded-2xl border border-white/10 bg-[#264653]/92 p-2 shadow-[0_12px_40px_rgba(38,70,83,0.45)] backdrop-blur-xl">
          <Link
            href="/sesiune-cunoastere"
            onClick={() => trackEvent("demo_promo_click", { location: "mobile_dock" })}
            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-[#DC7050] text-[15px] font-semibold text-white shadow-[0_4px_16px_rgba(220,112,80,0.40)] active:scale-[0.98]"
          >
            <Sparkles className="size-4" />
            <span className="whitespace-nowrap">Sesiune de cunoaștere gratuită</span>
          </Link>
          <Link
            href="/zenyra"
            onClick={() => trackEvent("zenyra_cta_click", { location: "mobile_dock" })}
            aria-label="Vorbește cu Zenyra"
            className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-white/10 active:scale-[0.95]"
          >
            <Image src="/Zenyra.svg" alt="" width={24} height={24} className="size-6" />
          </Link>
        </div>
      </div>
    </>
  );
}

// ─── Mobile helpers ─────────────────────────────────────────────────────────

function MobileSection({ title }: { title: string }) {
  return (
    <p className="mt-2 mb-1 px-2 pt-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#264653]/45">
      {title}
    </p>
  );
}

function MobileLink({
  href,
  children,
  onClose,
  accent,
}: {
  href: string;
  children: React.ReactNode;
  onClose: () => void;
  accent?: boolean;
}) {
  return (
    <Link
      href={href}
      onClick={onClose}
      className={cn(
        "block rounded-md px-2 py-2.5 text-[14px] font-medium transition active:bg-[#FDF9F5]",
        accent ? "text-[#2C969B]" : "text-[#264653]"
      )}
    >
      {children}
    </Link>
  );
}
